import { useCallback, useMemo } from 'react';

import { useCsvDownloadContext } from '@/components/Chart/context/CsvTableContext';
import ExportButton from '@/components/Chart/ExportButton';
import { Permission } from '@/entities/permissions';
import type { CsvWidgetExportSource } from '@/features/DataHub/modules/Export/types/context.types';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { ExportSelectionSource } from '@/hooks/useOpenExportSelection';
import usePermission from '@/shared/hooks/usePermission';
import { openExportDrawer } from '@/slices/drawerSlice';
import { setExport } from '@/slices/exportSlice';

export const CsvWidgetExportButton = ({ tabName }: { tabName?: string }) => {
  const { hasPermission } = usePermission();
  const { downloadFileId, selectedRange, fileName, groupName, dateColumnKey } =
    useCsvDownloadContext();
  const dispatch = useAppDispatch();
  const isPermissionGranted = hasPermission(Permission.ExportBasic);
  const source = useMemo<CsvWidgetExportSource | null>(() => {
    if (downloadFileId) {
      return { sourceType: 'query', queryFileId: downloadFileId, selectedRange };
    }
    if (fileName && groupName) {
      return { sourceType: 'datalake', fileName, groupName };
    }
    return null;
  }, [downloadFileId, selectedRange, fileName, groupName]);
  const isExportSourceAvailable = source !== null;
  const isButtonDisabled = !isPermissionGranted || !isExportSourceAvailable;

  const handleClick = useCallback(() => {
    if (!source) return;

    dispatch(
      setExport({
        selectedTimeRange:
          source.sourceType === 'query' ? (source.selectedRange ?? undefined) : undefined
      })
    );
    dispatch(
      openExportDrawer({
        showHistory: false,
        source: ExportSelectionSource.WIDGET_BUTTON,
        csvWidgetExportContext: { source, dateColumnKey: dateColumnKey ?? null, tabName }
      })
    );
  }, [dateColumnKey, dispatch, source, tabName]);

  return (
    <ExportButton
      disabled={isButtonDisabled}
      handleClick={handleClick}
      title='Download as CSV'
    />
  );
};
